import { SystemPath } from './cep';
import { makeDir, getSystemPath } from './fileHelpers';
import { isDirAvailableLocally, isFileAvailableLocally, readFile, writeFile } from '@mimir/common/util/fs';
import type { Preset } from './interfaces';
import type { NleAsset, TenantConfigDto } from '@mimir/common/dto/tenantModel';
import { getItemFields } from '@mimir/web/util/api';
import { isPremiereAssetVisibleToUser } from '@mimir/web/store/storeMisc';
import { getBasenameWithoutExtension } from '@mimir/common/platformAbstraction/pathComposition';
import { isInAdobeApp, type AdobePanelContext, type ElectronContext } from '@mimir/common/context/applicationContext';
import type { Logger } from '@mimir/common/types/logger';
import { invariant } from '@mimir/common/util/invariant';
import { makeStaticUrlProvider } from '@mimir/common/io/urlProvider';

const populateSamplePresets = (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  presetsPath: string,
) => {
  // copy sample preset file from extension installation location
  const extensionPath = getSystemPath(services.applicationContext, SystemPath.EXTENSION);
  const samplePresetsPath = `${extensionPath}/presets`;

  invariant(isInAdobeApp(services.applicationContext), 'populateSamplePresets should not be called outside of Adobe apps');
  const result = services.applicationContext.readdir(samplePresetsPath);
  if (!result || result.err || !result.data) {
    services.logger.error(`populateSamplePresets: result: ${result}, result.err: ${result && result.err}`);
    throw new Error('Unable to read sample presets');
  }

  for (const filename of result.data) {
    const readPath = `${samplePresetsPath}/${filename}`;
    const writePath = `${presetsPath}/${filename}`;
    const data = readFile(services.applicationContext.nodeApi, readPath);
    writeFile(services.applicationContext.nodeApi, writePath, data);
  }
};

export const getPresets = async (
  services: {
    logger: Logger,
    applicationContext: AdobePanelContext | ElectronContext,
  },
  downloadPath: string,
  tenantConfig: TenantConfigDto,
  dvrMode: boolean,
): Promise<Preset[]> => {
  if (tenantConfig.presets?.length) {
    return tenantConfig.presets.filter(p => isPremiereAssetVisibleToUser(p)).map(preset => ({
      name: preset.name,
      nleType: preset.nleType,
      itemId: preset.itemId,
      path: getPremierePresetDownloadPath(downloadPath, services.applicationContext, preset),
      language: preset.language,
    }));
  }

  if (dvrMode) {
    return [];
  }

  return getLocalPresets(services);
};

export const getLocalPresets = async (
  services: {
    logger: Logger,
    applicationContext: AdobePanelContext | ElectronContext,
  },
): Promise<Preset[]> => {
  const userDataPath = getSystemPath(services.applicationContext, SystemPath.USER_DATA);
  const mimirDataPath = `${userDataPath}/mimir-premiere-panel`;
  const presetsPath = `${mimirDataPath}/presets`;

  console.log(`Looking for presets in ${presetsPath}`);

  if (!await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, presetsPath)) {
    await makeDir(services, mimirDataPath);
    await makeDir(services, presetsPath);
    populateSamplePresets(services, presetsPath);
  }

  invariant(isInAdobeApp(services.applicationContext), 'populateSamplePresets should not be called outside of Adobe apps');
  const result = services.applicationContext.readdir(presetsPath);
  if (!result || result.err || !result.data) {
    console.error(`getPresets: result: ${result}, result.err: ${result && result.err}`);
    throw new Error('Unable to load presets');
  }

  const presetList: Preset[] = [];
  for (const filename of result.data) {
    const presetName = getBasenameWithoutExtension(filename, services.applicationContext.pathSep);
    const presetPath = `${presetsPath}/${filename}`;
    presetList.push({
      name: presetName,
      path: presetPath,
    });
  }

  return presetList;
};

const getPremierePresetDownloadPath = (
  downloadPath: string,
  adobeContext: AdobePanelContext | ElectronContext,
  preset: NleAsset,
) => {
  const ext = preset.nleType === 'dvr' ? '.xml' : '.epr';
  const localPath = `${downloadPath}${adobeContext.pathSep}${preset.itemId}${ext}`;
  return localPath;
};

export const downloadPresetIfNotAvailable = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  downloadPath: string,
  preset: NleAsset,
) => {
  const localPath = getPremierePresetDownloadPath(downloadPath, services.applicationContext, preset);

  if (await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, localPath)) {
    return localPath;
  }

  const { highRes } = await getItemFields(preset.itemId, ['highRes']);
  if (!highRes) {
    services.logger.warn(`Preset "${preset}" does not have a highres url!`);
    return;
  }

  await services.applicationContext.downloader.downloadFlatFile({ urlProvider: makeStaticUrlProvider(highRes), outputFilename: localPath });

  return localPath;
};
